import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { categories, sections } from "./categories";

// Header Bar Component - LIFE Score specific with progress tracking
const HeaderBar = ({ currentIndex }) => {
  // Memoize the section calculation to prevent recalculation on hover
  const { sectionIndex, sectionProgress } = useMemo(() => {
    const currentCategory = categories[currentIndex];
    const sectionIndex = sections.indexOf(currentCategory.category);
    const categoriesInSection = categories.filter(
      (cat) => cat.category === currentCategory.category
    );
    const categoryIndexInSection = categoriesInSection.findIndex(
      (cat) => cat.key === currentCategory.key
    );
    const sectionProgress =
      (categoryIndexInSection + 1) / categoriesInSection.length;

    return { sectionIndex, sectionProgress };
  }, [currentIndex]); // Only recalculate when currentIndex changes, NOT on hover

  const dots = Array.from({ length: 5 }, (_, i) => i);

  return (
    <>
      {/* Header with logo */}
      <div
        className="pt-6 pb-5 flex justify-center sticky top-0 z-50 backdrop-blur-sm"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
      >
        <img src="/images/logo-white.png" alt="Logo" className="h-6 w-auto" />
      </div>

      {/* Segmented progress bar with 5 dots */}
      <div
        className="w-full flex justify-center sticky top-14 z-50 pb-3"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0, 0, 0, 0.6), transparent)",
        }}
      >
        <div className="w-full max-w-md px-5">
          <div className="flex items-center">
            {dots.map((_, index) => (
              <React.Fragment key={index}>
                {/* Dot */}
                <div className="flex flex-col items-center relative z-10">
                  {index <= sectionIndex ? (
                    <motion.div
                      className="w-3 h-3 rounded-full border-2 flex items-center justify-center"
                      style={{
                        borderColor: "#dcff02",
                        backgroundColor: "#dcff02",
                      }}
                      animate={
                        index === sectionIndex
                          ? {
                              scale: [1, 1.15, 1],
                              boxShadow: [
                                "0 0 0 0 #dcff0200",
                                "0 0 0 2px #dcff0233",
                                "0 0 0 0 #dcff0200",
                              ],
                            }
                          : {}
                      }
                      transition={{
                        duration: 1.5,
                        times: [0, 0.5, 1],
                        repeat: Infinity,
                        repeatDelay: 3,
                      }}
                    >
                      {index < sectionIndex && (
                        <motion.svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-2 w-2 text-black"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </motion.svg>
                      )}
                    </motion.div>
                  ) : (
                    <div
                      className="w-3 h-3 rounded-full border-2 flex items-center justify-center"
                      style={{
                        borderColor: "#4B5563",
                        backgroundColor: "transparent",
                      }}
                    />
                  )}
                </div>

                {/* Connecting line between dots */}
                {index < dots.length - 1 && (
                  <div className="flex-1 h-[1.5px] relative -top-[0.5px] mx-0.5">
                    {index < sectionIndex ? (
                      <div
                        className="h-[1.5px]"
                        style={{ backgroundColor: "#dcff02" }}
                      />
                    ) : index === sectionIndex ? (
                      <div
                        className="h-[1.5px] w-full relative"
                        style={{ backgroundColor: "#4B5563" }}
                      >
                        <motion.div
                          className="h-[1.5px] absolute top-0 left-0"
                          style={{ backgroundColor: "#dcff02" }}
                          initial={{ width: 0 }}
                          animate={{ width: `${sectionProgress * 100}%` }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                        />
                      </div>
                    ) : (
                      <div
                        className="h-[1.5px]"
                        style={{ backgroundColor: "#4B5563" }}
                      />
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Current section title */}
          <motion.div
            className="text-center mt-2.5"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-medium" style={{ color: "#dcff02" }}>
              {sections[sectionIndex]}
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default HeaderBar;
